import { useState, useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useAppNavigate } from "../lib/route-primitives";
import { useQuery } from "@tanstack/react-query";
import { adminWeddingsApi, adminPlansApi } from "../lib/api";
import { Loader2, Calendar, MapPin, ChevronRight, Heart, Archive } from "lucide-react";
import { cn } from "../lib/utils";
import { paths } from "../lib/paths";
import { getPlanColors } from "../lib/plan-colors";

interface WeddingRow {
  id: string;
  slug: string;
  title: string;
  date_time: string | null;
  location: string | null;
  description: string | null;
  status: string;
  created_at: string;
  updated_at: string;
  owner_email: string | null;
  owner_name: string | null;
  owner_plan: string | null;
  /** Düğüne bağlı ödenmiş paket (sipariş) — paket düğündedir, hesapta değil (2026-10-15). */
  package_plan?: "pro" | "max" | null;
  package_order?: string | null;
  /** Paketsiz ama sahibinin hesabına özel plan atanmış (eski Pro/Max hesap ya da kurumsal). */
  account_wide?: boolean;
  /** Paket modeli (2026-10-16): sipariş / Free / geçiş paketi; taşınmış hesapta paketsiz düğün askıda. */
  package_kind?: "order" | "free" | "legacy" | "suspended" | null;
  package_label?: string | null;
}

const WEDDING_LIMIT = 50;

type StatusFilter = "active" | "archived" | "all";
interface PlanRow { id: string; slug: string; label: string; sort_order: number; }

function PlanBadge({ plan }: { plan: string | null }) {
  if (!plan) return <span className="text-muted-foreground/40 text-xs">—</span>;
  return (
    <span className={cn(
      "inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border whitespace-nowrap",
      getPlanColors(plan).badge
    )}>
      {plan}
    </span>
  );
}

/** Düğünün paketi: sipariş (plan + no) › Free / geçiş paketi › askıda › hesap geneli plan › ücretsiz deneme. */
function WeddingPackage({ w }: { w: WeddingRow }) {
  const { t } = useTranslation();
  if (w.package_plan) {
    return (
      <span className="inline-flex flex-col items-start gap-0.5" data-testid={`wedding-package-${w.slug}`}>
        <PlanBadge plan={w.package_plan} />
        {w.package_order && <span className="font-mono text-[10px] text-muted-foreground">{w.package_order}</span>}
      </span>
    );
  }
  if (w.package_kind === "free" || w.package_kind === "legacy") {
    return (
      <span className="inline-flex flex-col items-start gap-0.5" data-testid={`wedding-package-${w.slug}`}>
        <PlanBadge plan={w.package_kind === "free" ? "free" : (w.package_label ?? "").toLowerCase()} />
        <span className="text-[10px] text-muted-foreground">
          {w.package_kind === "free" ? t("admin.packageFree") : t("admin.packageLegacy")}
        </span>
      </span>
    );
  }
  if (w.package_kind === "suspended") {
    return (
      <span className="text-[11px] font-semibold text-red-600 dark:text-red-400" data-testid={`wedding-package-${w.slug}`}>
        {t("admin.packageSuspended")}
      </span>
    );
  }
  if (w.account_wide) {
    return (
      <span className="inline-flex flex-col items-start gap-0.5" data-testid={`wedding-package-${w.slug}`}>
        <PlanBadge plan={w.owner_plan} />
        <span className="text-[10px] text-muted-foreground">{t("admin.packageAccountWide")}</span>
      </span>
    );
  }
  return (
    <span className="text-[11px] text-muted-foreground" data-testid={`wedding-package-${w.slug}`}>
      {t("admin.packageTrial")}
    </span>
  );
}

/** Filtre: düğünün GEÇERLİ paketi (sipariş › Free / geçiş paketi › sahibinin hesap planı). */
const effectivePlan = (w: WeddingRow) =>
  w.package_plan
  ?? (w.package_kind === "free" ? "free" : w.package_kind === "legacy" ? (w.package_label ?? "").toLowerCase() : null)
  ?? (w.package_kind === "suspended" ? null : w.owner_plan);

const STATUS_OPTS: { value: StatusFilter; label: string }[] = [
  { value: "active",   label: "Aktif" },
  { value: "archived", label: "Arşiv" },
  { value: "all",      label: "Tümü" },
];

export function WeddingsPage() {
  const { t, i18n } = useTranslation();
  const navigate = useAppNavigate();

  const [statusFilter, setStatusFilter] = useState<StatusFilter>("active");
  const [planFilter, setPlanFilter]     = useState("");

  const { data: plansData } = useQuery({
    queryKey: ["admin", "plans"],
    queryFn: () => adminPlansApi.get<PlanRow[]>("/"),
  });
  const plans = (plansData?.data ?? []).sort((a, b) => a.sort_order - b.sort_order);

  const [weddings, setWeddings]       = useState<WeddingRow[]>([]);
  const [isLoading, setIsLoading]     = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore]         = useState(false);

  const fetchWeddings = useCallback(async (status: StatusFilter) => {
    setIsLoading(true);
    setWeddings([]);
    try {
      const res = await adminWeddingsApi.get<WeddingRow[]>(
        `?limit=${WEDDING_LIMIT}&status=${status}`
      );
      const rows = res.data ?? [];
      setWeddings(rows);
      setHasMore(rows.length >= WEDDING_LIMIT);
    } catch {
      // ignore
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchMoreLoadingRef = useRef(false);
  const fetchMore = useCallback(async () => {
    if (fetchMoreLoadingRef.current || !weddings.length) return;
    fetchMoreLoadingRef.current = true;
    setLoadingMore(true);
    try {
      const cursor = weddings[weddings.length - 1].created_at;
      const res = await adminWeddingsApi.get<WeddingRow[]>(
        `?limit=${WEDDING_LIMIT}&status=${statusFilter}&cursor=${encodeURIComponent(cursor)}`
      );
      const rows = res.data ?? [];
      setWeddings((prev) => [...prev, ...rows]);
      setHasMore(rows.length >= WEDDING_LIMIT);
    } catch {
      // ignore
    } finally {
      fetchMoreLoadingRef.current = false;
      setLoadingMore(false);
    }
  }, [weddings, statusFilter]);

  useEffect(() => { fetchWeddings(statusFilter); }, [fetchWeddings, statusFilter]);

  // Plan filter is client-side (stable across pages, avoids extra backend complexity)
  const visible = planFilter
    ? weddings.filter((w) => effectivePlan(w) === planFilter)
    : weddings;

  const isArchived = (w: WeddingRow) => w.status === "archived";

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div>
        <h1 className="font-serif italic text-3xl text-foreground">{t("wedding.title")}</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {t("admin.weddingsDesc")}
        </p>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        {/* Status pill group */}
        <div className="flex items-center rounded-lg border border-border overflow-hidden">
          {STATUS_OPTS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => setStatusFilter(opt.value)}
              className={cn(
                "px-3.5 py-1.5 text-xs font-medium transition-colors cursor-pointer",
                statusFilter === opt.value
                  ? "bg-primary text-white"
                  : "text-muted-foreground hover:bg-primary/5 hover:text-foreground"
              )}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {/* Plan pill group */}
        {plans.length > 0 && (
          <div className="flex items-center rounded-lg border border-border overflow-hidden">
            <button
              onClick={() => setPlanFilter("")}
              className={cn("px-3.5 py-1.5 text-xs font-medium transition-colors cursor-pointer", planFilter === "" ? "bg-primary text-white" : "text-muted-foreground hover:bg-primary/5 hover:text-foreground")}
            >
              {t("common.all")}
            </button>
            {plans.map((p) => (
              <button
                key={p.slug}
                onClick={() => setPlanFilter(p.slug)}
                className={cn("px-3.5 py-1.5 text-xs font-medium transition-colors cursor-pointer", planFilter === p.slug ? "bg-primary text-white" : "text-muted-foreground hover:bg-primary/5 hover:text-foreground")}
              >
                {p.label}
              </button>
            ))}
          </div>
        )}

        {/* Result count */}
        {!isLoading && (
          <span className="text-xs text-muted-foreground ml-auto">
            {visible.length} düğün{hasMore ? "+" : ""}
          </span>
        )}
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-24">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : visible.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-24 gap-3 text-center">
          <div className="h-12 w-12 rounded-2xl bg-primary/10 flex items-center justify-center">
            <Heart className="h-6 w-6 text-primary" />
          </div>
          <p className="text-sm text-muted-foreground">{t("wedding.noWeddingsAdmin")}</p>
        </div>
      ) : (
        <>
          {/* ── Desktop table ───────────────────────────────────── */}
          <div className="hidden md:block rounded-2xl border border-border overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead className="border-b border-border bg-muted/30">
                  <tr>
                    <th className="px-5 py-3.5 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Düğün</th>
                    <th className="px-5 py-3.5 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Organizatör</th>
                    <th className="px-5 py-3.5 text-[10px] font-bold uppercase tracking-widest text-muted-foreground w-28">{t("admin.packageColumn")}</th>
                    <th className="px-5 py-3.5 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Tarih</th>
                    <th className="px-5 py-3.5 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Konum</th>
                    <th className="w-10" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {visible.map((w) => {
                    const archived = isArchived(w);
                    return (
                      <tr
                        key={w.id}
                        onClick={() => navigate(paths.wedding.dashboard(w.slug))}
                        className={cn(
                          "transition-colors cursor-pointer",
                          archived
                            ? "opacity-50 hover:opacity-70 hover:bg-muted/30"
                            : "hover:bg-primary/5"
                        )}
                      >
                        <td className="px-5 py-4">
                          <div className="flex items-center gap-2">
                            {archived && <Archive className="h-3.5 w-3.5 text-muted-foreground shrink-0" />}
                            <div>
                              <p className={cn(
                                "text-sm font-semibold leading-snug",
                                archived ? "line-through text-muted-foreground" : "text-foreground"
                              )}>
                                {w.title}
                              </p>
                              <span className="mt-0.5 inline-block font-mono text-[11px] text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                                /{w.slug}
                              </span>
                            </div>
                          </div>
                        </td>
                        <td className="px-5 py-4">
                          <p className="text-sm text-foreground">{w.owner_name ?? "—"}</p>
                          {w.owner_email && (
                            <p className="text-xs text-muted-foreground truncate max-w-[180px]">{w.owner_email}</p>
                          )}
                        </td>
                        <td className="px-5 py-4 w-28">
                          <WeddingPackage w={w} />
                        </td>
                        <td className="px-5 py-4 text-sm text-muted-foreground whitespace-nowrap">
                          {w.date_time
                            ? new Date(w.date_time).toLocaleDateString(i18n.language)
                            : <span className="text-muted-foreground/40">—</span>}
                        </td>
                        <td className="px-5 py-4 text-sm text-muted-foreground max-w-[160px]">
                          {w.location
                            ? <span className="flex items-center gap-1 truncate"><MapPin className="h-3 w-3 shrink-0" />{w.location}</span>
                            : <span className="text-muted-foreground/40">—</span>}
                        </td>
                        <td className="px-5 py-4 text-right">
                          <ChevronRight className="h-4 w-4 text-muted-foreground inline-block" />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* ── Mobile cards ────────────────────────────────────── */}
          <div className="md:hidden space-y-2">
            {visible.map((w) => {
              const archived = isArchived(w);
              return (
                <div
                  key={w.id}
                  onClick={() => navigate(paths.wedding.dashboard(w.slug))}
                  className={cn(
                    "flex items-center gap-3 bg-card border border-border rounded-2xl px-4 py-3.5 transition-colors cursor-pointer",
                    archived ? "opacity-50 hover:opacity-70" : "hover:bg-primary/5"
                  )}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      {archived && <Archive className="h-3.5 w-3.5 text-muted-foreground shrink-0" />}
                      <p className={cn(
                        "text-sm font-semibold truncate",
                        archived ? "line-through text-muted-foreground" : "text-foreground"
                      )}>
                        {w.title}
                      </p>
                      <WeddingPackage w={w} />
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
                      <span>{w.owner_name ?? w.owner_email ?? "—"}</span>
                      {w.date_time && (
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {new Date(w.date_time).toLocaleDateString(i18n.language)}
                        </span>
                      )}
                    </div>
                  </div>
                  <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Load more */}
      {hasMore && !isLoading && (
        <div className="flex justify-center pt-2">
          <button
            onClick={fetchMore}
            disabled={loadingMore}
            className="inline-flex items-center gap-2 rounded-lg border border-primary/20 px-5 py-2.5 text-sm font-medium text-foreground hover:bg-primary/5 transition-colors disabled:opacity-50 cursor-pointer"
          >
            {loadingMore && <Loader2 className="h-4 w-4 animate-spin" />}
            {loadingMore ? t("common.loading") : t("common.loadMore")}
          </button>
        </div>
      )}
    </div>
  );
}
